import React, { useEffect, useState, useRef } from "react";
import Image from "next/image";
import Head from "next/head";
import Link from "next/link";
import { BASE_ASSETS_URL } from "@constants/constants";
import testBuildEvaluateImage from "../assets/clm/test-build-evaluate.png";
import { Minus, Plus } from "lucide-react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import {
  CLMButtonPrimary,
  CLMButtonSecondary,
} from "components/CLM/v3/Buttons";
import ClmShowcaseSection from "components/CLM/ShowCase";
import FAQ2 from "components/CLM/FAQ2";
import AnimatedLogos from "components/customers/AnimatedLogos";
import { industryLeadersCard } from "@constants/customersData";
import CustomersMarquee from "components/CLM/Customers";

const cards = [
  {
    id: 1,
    image: "1.png",
  },
  {
    id: 2,
    image: "2.png",
  },
  {
    id: 3,
    image: "3.png",
  },
  {
    id: 4,
    image: "4.png",
  },
  {
    id: 5,
    image: "5.png",
  },
  {
    id: 6,
    image: "6.png",
  },
  {
    id: 7,
    image: "7.png",
  },
];

const customerServiceFaqData = [
  {
    question: "What makes BoloSign’s CLM different from other solutions?",
    answer:
      "Most CLMs focus on just document storage or eSignatures. BoloSign is designed to streamline the full contract lifecycle — from template creation and negotiation to signature, version tracking, clause redlines, and AI insights. It’s simple enough for teams to adopt quickly, but powerful enough for complex enterprise workflows.",
  },
  {
    question: "Can BoloSign integrate with the tools we already use?",
    answer:
      "Absolutely. BoloSign offers native and API-based integrations with Salesforce, HubSpot, Google Workspace, Slack, Outlook, and more. Whether it’s auto-generating a contract from a CRM or syncing status updates to Slack, your teams won’t have to change the way they work — we just make it faster.",
  },
  {
    question: "How secure is our data on BoloSign?",
    answer:
      "Very. BoloSign is built on enterprise-grade security standards, including SOC 2, ISO 27001, data encryption at rest and in transit, access controls, and detailed audit logs. We also support role-based permissions, so your sensitive data stays exactly where it should.",
  },
  {
    question:
      "Is BoloSign’s AI legally reliable for contract edits and summaries?",
    answer:
      "Yes, and here’s how: Our AI, Certius, doesn’t just guess — it works off your custom playbooks, fallback clauses, and negotiation history. You’re always in control, and every AI-suggested edit can be accepted, rejected, or modified. Think of it like a super-fast junior legal assistant that never sleeps.",
  },
  {
    question:
      "Can I manage contracts for multiple teams or business units in one place?",
    answer:
      "Yes — that’s exactly what BoloSign’s Workspaces and Legal Hubs are built for. You can create segmented contract folders, workflows, and templates for Legal, Sales, HR, Procurement, and more — all under one roof, with cross-team visibility and control.",
  },
  {
    question: "How fast can we get started with BoloSign?",
    answer:
      "Faster than most CLMs. You can import your existing contracts, set up clickwraps and eSign templates, and start tracking status in under a week. Our customer success team will guide you through onboarding, or you can go hands-on right away — it’s that intuitive.",
  },
];

const transparentFaqData = [
  {
    question: "Compliant with Global eSign Laws:",
    answer:
      "Ensure every document you send or sign adheres to global eSignature laws like ESIGN, UETA, and eIDAS—helping your business stay legally compliant across borders.",
    image: `${BASE_ASSETS_URL}/v2/clm/home/transparent-ai/1.png`,
  },
  {
    question: "End-to-end document encryption:",
    answer:
      "Keep your data secure from sender to signer with robust encryption protocols that ensure confidentiality, integrity, and trust at every step.",
    image: `${BASE_ASSETS_URL}/v2/clm/home/transparent-ai/2.png`,
  },
  {
    question: "Data governance",
    answer:
      "Your documents and data stay in your control. We don’t access, store, or share your information, ensuring full alignment with your organization’s data governance standards.",
    image: `${BASE_ASSETS_URL}/v2/clm/home/transparent-ai/3.png`,
  },
  {
    question: "Privacy & security",
    answer:
      "Every document is protected with strict privacy controls and enterprise-grade security, ensuring only authorized users can view, sign, or access your data",
    image: `${BASE_ASSETS_URL}/v2/clm/home/transparent-ai/4.png`,
  },
];

const enterprise = [
  {
    title: "Expert Support & Services",
    desc: "We configure signature workflows, enforce compliance, and enable every feature.",
    img: `${BASE_ASSETS_URL}/v2/clm/home/enterprise/1.png`,
    color: "#ffffff",
  },
  {
    title: "Worldwide Customer Success",
    desc: "Get best-practice guidance at launch, during updates, and at renewal.",
    img: `${BASE_ASSETS_URL}/v2/clm/home/enterprise/2.png`,
    color: "#ffffff",
  },
  {
    title: "24/7 Support Access",
    desc: "Access live support via chat, email, or phone—24/7.",
    img: `${BASE_ASSETS_URL}/v2/clm/home/enterprise/3.png`,
    color: "#000000",
  },
  {
    title: "Custom Plans for Business Continuity",
    desc: "Enjoy SLA-backed uptime guarantees and priority issue resolution.",
    img: `${BASE_ASSETS_URL}/v2/clm/home/enterprise/4.png`,
    color: "#000000",
  },
];
export const showcaseData = [
  {
    id: 1,
    navText: "Click Through",
    imgSrc: `${BASE_ASSETS_URL}/v2/clm/home/showcase/Clickthrough.gif`,
    // icon: "https://sendbird.imgix.net/cms/Anywhere-logo.svg",
    heading: "Centralized Terms Management",
    desc: "Quickly update, publish, and track your terms—ensuring users always access the latest version without IT delays.",
    buttonText: "See how it works",
    buttonClickURL: "/clm/clickThrough",
    videoLength: 12,
  },
  {
    id: 2,
    navText: "Repository",
    imgSrc: `${BASE_ASSETS_URL}/v2/clm/home/showcase/Repository.gif`,
    // icon: "https://sendbird.imgix.net/cms/Anywhere-logo.svg",
    heading: "Smart, centralized contract management",
    desc: "BoloSign centralizes your contracts, automatically enriches metadata with AI, and ensures quick, secure access to essential contract details.",
    buttonText: "Learn more",
    buttonClickURL: "/clm/repository",
    videoLength: 9,
  },
  {
    id: 3,
    navText: "BoloSign AI",
    videoURL: `${BASE_ASSETS_URL}/v2/clm/videos/home/showcase/BoloSignAI.mp4`,
    // icon: "https://sendbird.imgix.net/cms/Anywhere-logo.svg",
    heading: "Auto-Generate Smart Intake Forms",
    desc: "AI transforms contract fields into dynamic questionnaires, collecting accurate inputs from business teams without legal back-and-forth.",
    buttonText: "Explore the platform",
    buttonClickURL: "/clm/",
    videoLength: 11, // in seconds
  },
  {
    id: 4,
    navText: "Legal AI",
    videoURL: `${BASE_ASSETS_URL}/v2/clm/videos/home/showcase/LegalAgent.mp4`,
    // icon: "https://sendbird.imgix.net/cms/Anywhere-logo.svg",
    heading: "Context-Aware Legal AI",
    desc: "AI trained on legal language, clause types, and context—so it gets what “termination for convenience” actually means.",
    buttonText: "Discover now",
    buttonClickURL: "/clm/ai-assistant",
    videoLength: 12,
  },
];

const CardCycle = () => {
  const MAX_VISIBLE = 4;
  const [cardQueue, setCardQueue] = useState(cards);
  const [exitingId, setExitingId] = useState(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setExitingId(cardQueue[0].id);

      setTimeout(() => {
        setCardQueue((prev) => {
          const [first, ...rest] = prev;
          return [...rest, first];
        });
        setExitingId(null);
      }, 200);
    }, 3000);

    return () => clearInterval(interval);
  }, [cardQueue]);

  const visibleCards = cardQueue.slice(0, MAX_VISIBLE);

  return (
    <div className="h-80 w-full px-4 md:px-0 flex justify-center items-center absolute bottom-[-160px]">
      <div className="relative w-full md:w-[475px] h-[160px]">
        <AnimatePresence>
          {visibleCards.map((card, index) => (
            <motion.div
              key={card.id}
              layout
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={{
                scale: 1 - index * 0.1,
                y: exitingId === card.id ? 40 : index * -25,
                opacity: exitingId === card.id ? 0 : 1 - index * 0.1,
                zIndex: cards.length - index,
                filter: `blur(${index}px)`,
              }}
              exit={{ opacity: 0, y: 40, transition: { duration: 0.3 } }}
              transition={{ duration: 0.4, ease: "easeInOut" }}
              className="absolute w-full md:w-auto top-0 left-0 right-0 bg-white rounded-xl shadow-lg text-sm p-4"
            >
              <Image
                src={`${BASE_ASSETS_URL}/v2/clm/home/top-cards/${card.image}`}
                width={534}
                height={188}
                alt="AI agent"
                loading="lazy"
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};

function FaqList({ faqs }) {
  const [openIndex, setOpenIndex] = useState(null);
  const currentImage = faqs[openIndex || 0]?.image;
  return (
    <div className="flex md:flex-row flex-col gap-8 mt-6">
      <div className="flex-1 md:block hidden">
        <Image
          src={currentImage}
          alt="transparent-ai"
          className="flex-1 rounded-xl"
          width={620}
          height={430}
          loading="lazy"
        />
      </div>
      <div className="flex-1">
        <div className="border-t border-gray-300">
          {faqs.map((faq, index) => (
            <FaqItem
              key={index}
              faq={faq}
              isOpen={openIndex === index}
              onToggle={() => setOpenIndex(openIndex === index ? null : index)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function FaqItem({ faq, isOpen, onToggle }) {
  const { question, answer, image } = faq;
  const contentRef = useRef(null);
  const [height, setHeight] = useState(0);
  useEffect(() => {
    if (isOpen && contentRef.current) {
      setHeight(contentRef.current.scrollHeight);
    } else {
      setHeight(0);
    }
  }, [isOpen]);

  return (
    <div className="px-4 py-5 border-b border-gray-300 transition-all duration-300">
      <button
        onClick={onToggle}
        className="flex justify-between items-center w-full text-left"
      >
        <h3 className=" text-[20px] leading-[26px] font-semibold">
          {question}
        </h3>
        <span className="text-purple-600 text-xl border-2 border-purple-600 rounded-full p-1">
          {isOpen ? <Minus size={20} /> : <Plus size={20} />}
        </span>
      </button>

      <div
        ref={contentRef}
        style={{
          maxHeight: `${height}px`,
          overflow: "hidden",
          transition: "max-height 0.3s ease, opacity 0.3s ease",
          opacity: isOpen ? 1 : 0,
        }}
      >
        <p className="mt-3 text-gray-700 text-[18px] leading-[26px]">
          {answer}
        </p>
        <div className="flex-1 md:hidden block mt-3">
          <Image
            src={image}
            alt="transparent-ai"
            className="flex-1"
            width={620}
            height={430}
            loading="lazy"
          />
        </div>
      </div>
    </div>
  );
}

const Home = () => {
  function GradientHeading() {
    const headingRef = useRef(null);
    const inView = useInView(headingRef, {
      amount: 0.4,
      once: false,
    });

    const gradientText = (text) => (
      <span
        className={`inline-block mr-1 transition-colors duration-700 ${
          inView
            ? "bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 bg-clip-text text-transparent"
            : "text-gray-400"
        }`}
      >
        {text}
      </span>
    );

    return (
      <div
        ref={headingRef}
        className={`
        transition-opacity duration-[1000ms] 
        ${inView ? "opacity-100" : "opacity-50"}
        text-gray-800
      `}
      >
        <h2 className="text-4xl md:text-[60px] md:leading-[64px] mx-auto text-center font-normal">
          <div className="block">
            The {gradientText("AI workforce")} humans can lean on.
          </div>
          <div className="block">
            Never tires, never quits, and {gradientText("always improves")}
          </div>
          <div className="block">
            Scale to meet {gradientText("any demand")}.
          </div>
        </h2>
        <p className="mt-4 text-gray-600 w-[70%] md:w-full mx-auto text-base md:text-lg text-center ">
          End-to-End Contract Automation with CLM, eSignatures, and AI—All in
          One Place
        </p>
      </div>
    );
  }

  return (
    <>
      <Head>
        <title>BoloSign - AI for Smart Contract Management</title>
        <meta
          name="description"
          content="BoloSign is your AI assistant for smart contract management. Draft, review, and eSign contracts on autopilot with our all-in-one CLM solution."
        />
        <meta
          name="keywords"
          content="BoloSign, AI contract management, smart contract management, CLM, eSignatures, legal AI, contract automation"
        />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <link rel="canonical" href="https://www.bolosign.com/" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="BoloSign - AI for Smart Contract Management"
        />
        <meta
          property="og:description"
          content="BoloSign is your AI assistant for smart contract management. Draft, review, and eSign contracts on autopilot with our all-in-one CLM solution."
        />
        <meta
          property="og:image"
          content={`https://boloforms-internal-images.s3.ap-south-1.amazonaws.com/website/assets/v2/clm/media-viewport.png`}
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="BoloSign - AI for Smart Contract Management"
        />
        <meta
          name="twitter:description"
          content="BoloSign is your AI assistant for smart contract management. Draft, review, and eSign contracts on autopilot with our all-in-one CLM solution."
        />
        <meta
          name="twitter:image"
          content={`https://boloforms-internal-images.s3.ap-south-1.amazonaws.com/website/assets/v2/clm/media-viewport.png`}
        />

        <link
          rel="icon"
          href="https://boloforms-internal-images.s3.ap-south-1.amazonaws.com/website/assets/v2/clm/Logo.png"
        />
      </Head>
      <section className="text-white relative h-[600px] md:h-[750px] bg-dark">
        <video
          autoPlay
          loop
          muted
          className="absolute top-0 left-0 w-full h-full object-cover z-0 overflow-hidden"
        >
          <source
            src={`${BASE_ASSETS_URL}/v2/clm/videos/bgAnimation.mp4`}
            type="video/mp4"
          />
        </video>
        <div className="relative z-10 flex flex-col items-center justify-start gap-6 h-full">
          <div className="flex flex-col items-center justify-center gap-4 md:gap-6 md:mt-20 mt-8 w-full ">
            <h1 className="text-4xl leading-[48px] md:text-[92px] md:leading-[92px] text-center max-w-full md:max-w-[950px] font-semibold">
              AI for Smart Contract Management
            </h1>
            <p className="text-base md:text-2xl text-center max-w-[750px] w-[70%] md:w-full">
              Your AI assistant to draft, review, and eSign contracts on
              autopilot.
            </p>

            <CLMButtonSecondary
              buttonText="Request Demo"
              buttonUrl="clm/request-demo"
            />

            <div className="w-[100%] md:w-[90%] relative mt-2">
              <div className="pointer-events-none absolute left-0 top-0 h-full w-24 bg-gradient-to-r from-[#1b1b1b] to-transparent z-10 hidden md:block" />
              <div className="pointer-events-none absolute right-0 top-0 h-full w-24 bg-gradient-to-l from-[#1b1b1b] to-transparent z-10 hidden md:block" />
              <AnimatedLogos />
            </div>
          </div>
          <CardCycle />
        </div>
      </section>

      {/* AI workspace section */}
      <section className=" text-black py-16 px-2 md:px-8 text-center md:mt-32 mt-16 z-10 relative max-w-screen-xl mx-auto ">
        <GradientHeading />

        {/* <div className="flex justify-between text-xxxs md:text-base text-gray-600 mt-20 px-2 md:px-12">
          <span className="text-start">Omnichannel</span>
          <span className="text-center ml-10">
            AI agent engine, powered by Sendbird
          </span>
          <span className="text-end">Your support software</span>
        </div> */}
        {/* test build evaluate image */}
        <div className="px-2 md:px-0 mt-12 mb-8">
          {/* I want the image to contain full width */}
          <video
            src={`${BASE_ASSETS_URL}/v2/clm/videos/home/workspace.mp4`}
            className="w-full"
            autoPlay
            loop
            muted
          />
        </div>

        {/* Button with text View AI Agent Platform */}
        <CLMButtonPrimary buttonText="View AI Agent Platform" buttonUrl={""} />
      </section>

      {/* showcase customers */}
      <section className="bg-[#f4f2f0]">
        <div className="max-w-screen-xl md:px-8 px-4 mx-auto py-20 text-gray-900 text-center">
          <ClmShowcaseSection
            showcaseData={showcaseData}
            heading={"A Complete Contract Management Solution"}
            desc={
              "One-click approvals, a secure document repository, and built-in Legal AI for instant drafting, review, and signing."
            }
          />
          {/* command your ai div` */}
          <div className="flex md:flex-row flex-col md:gap-2 gap-8 mt-36 mb-10 justify-center items-center w-full">
            {/* first div */}
            <div className="flex-1">
              <div className="md:w-[80%] md:text-left text-center">
                <h3 className="text-4xl md:text-5xl font-semibold leading-snug ">
                  Contracting made simple: CLM + eSignature + AI
                </h3>
                <p className="mt-6 md:text-[22px] text-gray-800 mb-10">
                  Simplify contract creation, accelerate signing, and automate
                  approvals—with AI at every step.
                </p>
                <CLMButtonPrimary
                  buttonText="View AI Agent Builder"
                  buttonUrl="/clm/signature"
                />
              </div>
            </div>
            {/* Video */}
            <div className="md:w-[650px] max-w-full  rounded-[20px] overflow-hidden ">
              <Image
                src={`${BASE_ASSETS_URL}/v2/clm/home/command-ai.gif`}
                alt="test-build-evaluate"
                width={100}
                height={100}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* connect the world section */}
      <section className="bg-[#0d0d0d] pb-20">
        <div className="text-white py-16 md:px-8 px-4 max-w-screen-xl mx-auto">
          <h3
            className="text-4xl md:text-5xl md:mt-10 mt-8 font-semibold md:text-left text-center"
            style={{ lineHeight: 1.1 }}
          >
            Connecting the world with AI agents. <br /> 5 million users and
            counting.
          </h3>
          <div className="grid grid-cols-4 grid-rows-2  gap-3 mt-10">
            {/* 2x2 box on the left */}
            <div className="md:col-span-2 md:row-span-2 col-span-4  relative rounded-xl overflow-hidden md:h-auto h-[170px]">
              <Image
                src={`${BASE_ASSETS_URL}/v2/clm/home/connecting-world/1.png`}
                width={498}
                height={700}
                alt="Scalable and compliant"
                className="object-cover w-full h-full"
                loading="lazy"
              />
              <div className="absolute top-0 left-0 w-full h-full md:w-[70%] md:p-10 p-4">
                <p className="text-left md:text-5xl text-3xl font-semibold">
                  Secure and Compliant
                </p>
                <Image
                  src={`${BASE_ASSETS_URL}/v2/clm/clm/inside-globe.png`}
                  // src={require("../assets/clm/inside-globe.png")}
                  alt="inside-globe"
                  className=" h-[46px] md:h-[78px] w-auto md:mt-5 mt-2"
                  width={46}
                  height={78}
                  loading="lazy"
                />
              </div>
            </div>

            {/* 1x2 box on top right */}
            <div className="md:col-span-2 md:row-span-1 col-span-4 relative rounded-xl overflow-hidden   md:h-auto h-[170px]">
              <Image
                src={`${BASE_ASSETS_URL}/v2/clm/home/connecting-world/2.png`}
                alt="meeting"
                className="object-cover w-full h-full"
                width="200"
                height="100"
                loading="lazy"
              />
              <div className="absolute top-0 left-0 w-full h-full  md:p-6 p-4">
                <p className="text-left  md:text-6xl text-3xl font-semibold">
                  300k+
                </p>
                <p className="text-left text-xl">Organizations globally</p>
              </div>
            </div>

            {/* Bottom right - two 1x1 boxes */}
            <div className=" md:col-span-1 col-span-2 rounded-xl relative overflow-hidden">
              <Image
                src={`${BASE_ASSETS_URL}/v2/clm/home/connecting-world/3.png`}
                alt="graph"
                className="object-cover w-full h-full"
                width="200"
                height="100"
                loading="lazy"
              />
              <div className="absolute top-0 left-0 w-full h-full  md:p-6 p-4">
                <p className="text-left  md:text-6xl text-3xl font-semibold">
                  150K+
                </p>
                <p className="text-left md:text-xl text-base">
                  Documents signed monthly
                </p>
              </div>
            </div>
            <div className="md:col-span-1 col-span-2 rounded-xl overflow-hidden relative ">
              <Image
                src={`${BASE_ASSETS_URL}/v2/clm/home/connecting-world/4.png`}
                alt="uptime"
                className="object-cover w-full h-full"
                width="100"
                height="100"
                loading="lazy"
              />
              <div className="absolute top-0 left-0 w-full h-full  md:p-6 p-4">
                <p className="text-left  md:text-6xl text-3xl font-semibold">
                  99.95%
                </p>
                <p className="text-left md:text-xl text-base">
                  Platform availability
                </p>
              </div>
            </div>
          </div>

          <h3
            className="text-4xl md:text-5xl md:text-left text-center font-semibold md:mt-44 mt-20"
            style={{ lineHeight: 1.1 }}
          >
            Industry leaders fly with BoloSign <br /> It's your turn to rise
            with AI.
          </h3>
        </div>
        <CustomersMarquee customersData={industryLeadersCard} />
      </section>

      {/* transparent AI section */}
      <section className="bg-[#f4f2f0] ">
        <div className="text-black py-16 md:px-8 px-4 max-w-screen-xl mx-auto text-left">
          <h3 className="text-4xl md:text-5xl  leading-snug md:text-left text-center font-semibold">
            Legally compliant eSignatures on a secure platform.
          </h3>
          <p className="mt-6 text-gray-600 md:text-[22px] md:leading-[31px] text-[18px]  md:text-left text-center">
            Digitally sign documents — built for businesses that prioritize
            security, compliance, and peace of mind.
          </p>
          <FaqList faqs={transparentFaqData} />

          {/* enterprise div */}
          <div className="">
            <h2 className="text-4xl md:text-5xl  leading-snug md:mt-48 mt-24 md:text-left text-center font-semibold">
              Enterprise-Grade Support for Your Digital Workflows
            </h2>
            <p className="mt-6 text-gray-600 md:text-[22px] leading-[31px] md:text-left text-center text-[18px]">
              Our team brings deep expertise in eSignatures, automation,
              integrations, and document workflows—helping you confidently scale
              with BoloSign.
            </p>

            <div
              className="mt-8 flex gap-4 md:overflow-x-hidden  overflow-x-auto overflow-y-hidden snap-x snap-mandatory"
              style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            >
              {enterprise.map((item, index) => (
                <div
                  key={index}
                  className="relative flex-shrink-0 md:flex-shrink snap-start"
                >
                  <Image
                    src={item.img}
                    alt={item.title}
                    width={305}
                    height={480}
                    className="rounded-2xl"
                    loading="lazy"
                  />
                  <div className="w-[285px] h-[480px] absolute top-0 left-0 px-[24px] py-8">
                    <h3
                      className=" text-[26px] font-bold leading-[28px]"
                      style={{
                        color: item.color || "#fff",
                      }}
                    >
                      {item.title}
                    </h3>
                    <p
                      className={`text-[18px] leading-[22px] mt-6 font-medium`}
                      style={{
                        color: item.color || "#fff",
                      }}
                    >
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-[#f4f2f0] text-black py-16 md:px-10 px-4 xl:px-20 text-center md:pt-24 pt-12 ">
        <FAQ2 faqData={customerServiceFaqData} heading={"FAQs"} />
      </section>
    </>
  );
};

export default Home;
